import React, { useEffect, useRef, useState } from "react";
// Flag sprites for the country dropdown + phone-code selector (PhoneNumberInput);
// this bundle has no other importer of it, so the `fi fi-xx` flags need it here.
import "/node_modules/flag-icons/css/flag-icons.min.css";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { submitConsultation } from "../../../services/contactCenterService";
import {
  getConsultationTimeSlots,
  getConsultationCourseInterests,
  getKoreanLevels,
} from "../../../services/lookupService";
import { getCountry } from "../../../services/flag";
import { getCountryWithPhoneCode } from "../../../services/country";
import { useTranslation } from "react-i18next";
import { i18n } from "../../../localization/i18n";
import PhoneNumberInput from "../../input/PhoneNumberInput";
import {
  SuccessTemplate,
  ErrorTemplate,
} from "../../common/ToastMessage/Template";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  phoneCode: "",
  // Preferred contact method defaults to Email.
  contactMethod: "EMAIL",
  country: "",
  preferredTime: "",
  interestedCourse: "",
  // Korean proficiency defaults to the first level (Beginner / TOPIK 1).
  koreanLevel: "BEGINNER_1",
  request: "",
  consent: false,
};

// Preferred contact method — a fixed two-option list (Email default / Phone-WhatsApp).
const CONTACT_METHOD_OPTIONS = [
  { value: "EMAIL", labelKey: "CONTACT_US_CENTER.CONSULTATION.METHOD_EMAIL", default: "Email" },
  { value: "PHONE_WHATSAPP", labelKey: "CONTACT_US_CENTER.CONSULTATION.METHOD_PHONE", default: "Phone / WhatsApp" },
];

// English defaults for the consultation form's common_ns keys. Keeps the form
// self-sufficient (no dependency on the local translations.js bundle); the backend
// common_ns source overrides these once the keys are seeded from the exported CSV.
const DEFAULTS = {
  "CONTACT_US_CENTER.FORM.REQUIRED": "This field is required.",
  "CONTACT_US_CENTER.FORM.EMAIL_INVALID": "Please enter a valid email address.",
  "CONTACT_US_CENTER.FORM.CONSENT_REQUIRED": "Please agree to the consent.",
  "CONTACT_US_CENTER.FORM.SUCCESS_TITLE": "Submitted",
  "CONTACT_US_CENTER.FORM.ERROR_TITLE": "Submission failed",
  "CONTACT_US_CENTER.FORM.ERROR_DETAIL": "Something went wrong. Please try again later.",
  "CONTACT_US_CENTER.CONSULTATION.INTRO_TITLE": "🌟 Course Consultation & learning roadmap design",
  "CONTACT_US_CENTER.CONSULTATION.INTRO_TEXT":
    "Try Dotori Korean's premium lessons for free and get a personalized learning roadmap. Any global user can be guided by email.",
  "CONTACT_US_CENTER.CONSULTATION.NAME_LABEL": "Name",
  "CONTACT_US_CENTER.CONSULTATION.NAME_PLACEHOLDER": "Enter your name.",
  "CONTACT_US_CENTER.CONSULTATION.EMAIL_LABEL": "Email",
  "CONTACT_US_CENTER.CONSULTATION.EMAIL_PLACEHOLDER":
    "Enter the global email to receive replies and schedule guidance.",
  "CONTACT_US_CENTER.CONSULTATION.REQUEST_LABEL": "Request",
  "CONTACT_US_CENTER.CONSULTATION.REQUEST_PLACEHOLDER":
    "Briefly describe what you'd like to discuss or your current Korean level.",
  "CONTACT_US_CENTER.CONSULTATION.CONSENT":
    "I agree to the collection, use and overseas transfer of my personal information for the global service.",
  "CONTACT_US_CENTER.CONSULTATION.SUBMIT": "Submit consultation request",
  "CONTACT_US_CENTER.CONSULTATION.SUCCESS_DETAIL":
    "Your consultation request has been received. We'll guide you via the email you provided.",
};

// Basic email shape check (mirrors the LeaveMessagePopup form rule).
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Design-system field styling (matches the global LeaveMessagePopup form).
const INPUT_CLASS =
  "keduall-form input-default-default keduall-typography text-16-normal-400 text-default-txt";
const TEXTAREA_CLASS =
  "keduall-form textarea-default keduall-typography text-16-normal-400 text-default-txt p-3";

// Required-field marker rendered next to a label.
const RequiredMark = () => <span className="cuc-required">*</span>;

// Pick the option label for the current UI language, falling back to English.
const labelFor = (item, lang) =>
  (lang === "ko" ? item.nameKo : lang === "vi" ? item.nameVi : item.nameEn) ||
  item.nameEn ||
  item.code;

/** 수강 상담 (Course Consultation) — lead form. Posts to /api/EnrollmentConsultations. */
const ConsultationTab = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // Bumped on a successful submit to remount PhoneNumberInput (its number is internal
  // state seeded from the value prop only at mount, so clearing form.phone isn't enough).
  const [phoneKey, setPhoneKey] = useState(0);

  // Country dropdowns + localized option lists.
  const [countries, setCountries] = useState([]);
  const [phoneCountries, setPhoneCountries] = useState([]);
  const [countryCode, setCountryCode] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [courseInterests, setCourseInterests] = useState([]);
  const [koreanLevels, setKoreanLevels] = useState([]);

  // Tracks which fields the user has edited, so onBlur validation only fires once dirty.
  const [dirty, setDirty] = useState({});

  const toast = useRef(null);
  const { t } = useTranslation("common_ns", { useSuspense: false });
  // Resolve a common_ns key with an English default (explicit arg or the DEFAULTS map),
  // so the form does not depend on the local translations.js bundle for its labels.
  const tc = (key, dflt) => t(key, { defaultValue: dflt ?? DEFAULTS[key] ?? key });
  const lang = (i18n.language || "en").slice(0, 2);

  useEffect(() => {
    const load = async () => {
      try {
        const [
          countryData,
          phoneData,
          slots,
          interests,
          levels,
        ] = await Promise.all([
          getCountry(),
          getCountryWithPhoneCode(),
          getConsultationTimeSlots(),
          getConsultationCourseInterests(),
          getKoreanLevels(),
        ]);
        setCountries(countryData || []);
        setPhoneCountries(phoneData || []);
        setTimeSlots(slots || []);
        setCourseInterests(interests || []);
        setKoreanLevels(levels || []);
      } catch {
        // Non-fatal: the form still submits without the dropdown options.
      }
    };
    load();
  }, []);

  // Map a lookup result to PrimeReact { label, value } options for the language.
  const toOptions = (items) =>
    (items || []).map((it) => ({ label: labelFor(it, lang), value: it.code }));

  const set = (key) => (e) => {
    const value = e.target ? e.target.value : e.value;
    setForm((f) => ({ ...f, [key]: value }));
    setDirty((d) => (d[key] ? d : { ...d, [key]: true }));
    // Clear a field error as soon as the user edits it.
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  // Validate a single text field's rule. Used onBlur (only once the field is dirty)
  // for immediate feedback, mirroring the full-form validate() rules.
  const fieldError = (key, value) => {
    const v = (value ?? "").trim();
    if (key === "name") {
      return v ? undefined : tc("CONTACT_US_CENTER.FORM.REQUIRED");
    }
    if (key === "email") {
      if (!v) return tc("CONTACT_US_CENTER.FORM.REQUIRED");
      return EMAIL_REGEX.test(v)
        ? undefined
        : tc("CONTACT_US_CENTER.FORM.EMAIL_INVALID");
    }
    return undefined;
  };

  const blur = (key) => () => {
    if (!dirty[key]) return;
    setErrors((prev) => ({ ...prev, [key]: fieldError(key, form[key]) }));
  };

  // Per-field validation (required + email/phone format). Returns the error map.
  const validate = () => {
    const next = {};
    if (!form.name?.trim()) next.name = tc("CONTACT_US_CENTER.FORM.REQUIRED");
    if (!form.email?.trim()) next.email = tc("CONTACT_US_CENTER.FORM.REQUIRED");
    else if (!EMAIL_REGEX.test(form.email.trim()))
      next.email = tc("CONTACT_US_CENTER.FORM.EMAIL_INVALID");
    if (!form.phone?.trim())
      next.phone = tc("CONTACT_US_CENTER.CONSULTATION.PHONE_INVALID", "Please enter a valid phone number.");
    if (!form.consent)
      next.consent = tc("CONTACT_US_CENTER.FORM.CONSENT_REQUIRED");
    return next;
  };

  const showSuccess = () =>
    toast.current?.show({
      severity: "success",
      summary: tc("CONTACT_US_CENTER.FORM.SUCCESS_TITLE"),
      detail: tc("CONTACT_US_CENTER.CONSULTATION.SUCCESS_DETAIL"),
      life: 4000,
      className: "keduall-toast",
      content: (props) => SuccessTemplate(props),
    });

  const showError = () =>
    toast.current?.show({
      severity: "error",
      summary: tc("CONTACT_US_CENTER.FORM.ERROR_TITLE"),
      detail: tc("CONTACT_US_CENTER.FORM.ERROR_DETAIL"),
      life: 4000,
      className: "keduall-toast",
      content: (props) => ErrorTemplate(props),
    });

  const onSubmit = async () => {
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSubmitting(true);
    try {
      await submitConsultation(form);
      setForm(EMPTY);
      setErrors({});
      setDirty({});
      // Remount the phone input so its internal number field resets too.
      setCountryCode(null);
      setPhoneKey((k) => k + 1);
      showSuccess();
    } catch {
      showError();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cuc-form">
      <Toast ref={toast} position="bottom-right" className="keduall-toast" />
      <div className="cuc-form-intro">
        <h3>{tc("CONTACT_US_CENTER.CONSULTATION.INTRO_TITLE")}</h3>
        <p>{tc("CONTACT_US_CENTER.CONSULTATION.INTRO_TEXT")}</p>
      </div>

      <div className="cuc-form-grid">
        {/* Name */}
        <div className="cuc-field">
          <label>
            {tc("CONTACT_US_CENTER.CONSULTATION.NAME_LABEL")}
            <RequiredMark />
          </label>
          <InputText
            className={`${INPUT_CLASS}${errors.name ? " p-invalid" : ""}`}
            value={form.name}
            onChange={set("name")}
            onBlur={blur("name")}
            placeholder={tc("CONTACT_US_CENTER.CONSULTATION.NAME_PLACEHOLDER")}
          />
          {errors.name && <small className="cuc-field-error">{errors.name}</small>}
        </div>

        {/* Email */}
        <div className="cuc-field">
          <label>
            {tc("CONTACT_US_CENTER.CONSULTATION.EMAIL_LABEL")}
            <RequiredMark />
          </label>
          <InputText
            className={`${INPUT_CLASS}${errors.email ? " p-invalid" : ""}`}
            value={form.email}
            onChange={set("email")}
            onBlur={blur("email")}
            placeholder={tc("CONTACT_US_CENTER.CONSULTATION.EMAIL_PLACEHOLDER")}
          />
          {errors.email && (
            <small className="cuc-field-error">{errors.email}</small>
          )}
        </div>

        {/* Phone / WhatsApp */}
        <div className="cuc-field">
          <label>
            {tc("CONTACT_US_CENTER.CONSULTATION.PHONE_LABEL", "Phone / WhatsApp")}
            <RequiredMark />
          </label>
          <PhoneNumberInput
            key={phoneKey}
            value={form.phone}
            country={countryCode}
            options={phoneCountries}
            placeholder={tc("CONTACT_US_CENTER.CONSULTATION.PHONE_PLACEHOLDER", "Enter your phone number incl. country code.")}
            invalidMessage={tc("CONTACT_US_CENTER.CONSULTATION.PHONE_INVALID", "Please enter a valid phone number.")}
            hasError={!!errors.phone}
            onchange={(data) => {
              setForm((f) => ({ ...f, phone: data.value }));
              if (data.data?.dialCode) {
                setForm((f) => ({ ...f, phoneCode: data.data.dialCode }));
              }
              if (data.data?.country) {
                setCountryCode(data.data.country);
              }
              setErrors((prev) =>
                prev.phone ? { ...prev, phone: undefined } : prev
              );
            }}
          />
        </div>

        {/* Preferred contact method */}
        <div className="cuc-field">
          <label>{tc("CONTACT_US_CENTER.CONSULTATION.METHOD_LABEL", "Preferred contact method")}</label>
          <Dropdown
            className={INPUT_CLASS}
            value={form.contactMethod}
            options={CONTACT_METHOD_OPTIONS.map((o) => ({
              label: t(o.labelKey, { defaultValue: o.default }),
              value: o.value,
            }))}
            onChange={set("contactMethod")}
          />
        </div>

        {/* Residence country */}
        <div className="cuc-field">
          <label>{tc("CONTACT_US_CENTER.CONSULTATION.COUNTRY_LABEL", "Country")}</label>
          <Dropdown
            className={INPUT_CLASS}
            value={form.country}
            options={countries}
            optionLabel="name"
            optionValue="code"
            filter
            onChange={set("country")}
            placeholder={tc("CONTACT_US_CENTER.CONSULTATION.SELECT_PLACEHOLDER", "Please select")}
            itemTemplate={(c) => (
              <span>
                <span className={`fi fi-${c.code.toLowerCase()} mr-2`} />
                {c.name}
              </span>
            )}
          />
        </div>

        {/* Preferred consultation time */}
        <div className="cuc-field">
          <label>{tc("CONTACT_US_CENTER.CONSULTATION.TIME_LABEL", "Preferred consultation time")}</label>
          <Dropdown
            className={INPUT_CLASS}
            value={form.preferredTime}
            options={toOptions(timeSlots)}
            onChange={set("preferredTime")}
            placeholder={tc("CONTACT_US_CENTER.CONSULTATION.SELECT_PLACEHOLDER", "Please select")}
          />
        </div>

        {/* Interested course */}
        <div className="cuc-field">
          <label>{tc("CONTACT_US_CENTER.CONSULTATION.COURSE_LABEL", "Interested course")}</label>
          <Dropdown
            className={INPUT_CLASS}
            value={form.interestedCourse}
            options={toOptions(courseInterests)}
            onChange={set("interestedCourse")}
            placeholder={tc("CONTACT_US_CENTER.CONSULTATION.SELECT_PLACEHOLDER", "Please select")}
          />
        </div>

        {/* Current Korean level */}
        <div className="cuc-field">
          <label>{tc("CONTACT_US_CENTER.CONSULTATION.LEVEL_LABEL", "Current Korean level")}</label>
          <Dropdown
            className={INPUT_CLASS}
            value={form.koreanLevel}
            options={toOptions(koreanLevels)}
            onChange={set("koreanLevel")}
            placeholder={tc("CONTACT_US_CENTER.CONSULTATION.SELECT_PLACEHOLDER", "Please select")}
          />
        </div>
      </div>

      {/* Request */}
      <div className="cuc-field">
        <label>{tc("CONTACT_US_CENTER.CONSULTATION.REQUEST_LABEL")}</label>
        <InputTextarea
          className={TEXTAREA_CLASS}
          rows={5}
          value={form.request}
          onChange={set("request")}
          placeholder={tc("CONTACT_US_CENTER.CONSULTATION.REQUEST_PLACEHOLDER")}
        />
      </div>

      <div className="cuc-field">
        <label className={`cuc-consent${errors.consent ? " cuc-consent-error" : ""}`}>
          <Checkbox
            checked={form.consent}
            onChange={(e) => {
              setForm((f) => ({ ...f, consent: e.checked }));
              setErrors((prev) =>
                prev.consent ? { ...prev, consent: undefined } : prev
              );
            }}
          />
          {tc("CONTACT_US_CENTER.CONSULTATION.CONSENT")}
        </label>
        {errors.consent && (
          <small className="cuc-field-error">{errors.consent}</small>
        )}
      </div>

      <Button
        className="cuc-form-submit keduall-buttons button-filled-default-normal-primary text-white-txt keduall-typography heading-h6-semibold-600"
        icon="pi pi-send"
        label={tc("CONTACT_US_CENTER.CONSULTATION.SUBMIT")}
        loading={submitting}
        disabled={!form.consent}
        onClick={onSubmit}
      />
    </div>
  );
};

export default ConsultationTab;
